import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tende } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Tent, Plus, Edit, Trash2, Users, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Risorse() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTenda, setSelectedTenda] = useState(null);
  const [form, setForm] = useState({
    codice: "", modello: "", posti_totali: 4, ubicazione: "", note: ""
  });

  const queryClient = useQueryClient();
  const { data: tendeData } = useQuery({ queryKey: ['tende'], queryFn: tende.list });

  const createMutation = useMutation({
    mutationFn: tende.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['tende']);
      setDialogOpen(false);
      resetForm();
      toast.success("Tenda aggiunta");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => tende.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tende']);
      setDialogOpen(false);
      setSelectedTenda(null);
      toast.success("Tenda aggiornata");
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: tende.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['tende']);
      toast.success("Tenda eliminata");
    },
  });

  const resetForm = () => setForm({
    codice: "", modello: "", posti_totali: 4, ubicazione: "", note: ""
  });

  const handleEdit = (t) => {
    setSelectedTenda(t);
    setForm({ ...t });
    setDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedTenda) {
      updateMutation.mutate({ id: selectedTenda.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const getOccupancyColor = (occupati, totali) => {
    const pct = (occupati / totali) * 100;
    if (pct >= 100) return "bg-red-500";
    if (pct >= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  const totTende = tendeData?.length || 0;
  const totPosti = tendeData?.reduce((acc, t) => acc + t.posti_totali, 0) || 0;
  const totOccupati = tendeData?.reduce((acc, t) => acc + (t.posti_occupati || 0), 0) || 0;
  const totDisponibili = totPosti - totOccupati;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="risorse-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Alloggi - Tende</h1>
        <p className="text-slate-500 mt-1">Gestione tende e assegnazione posti letto</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg"><Tent className="h-5 w-5 text-purple-600" /></div>
              <div>
                <p className="text-2xl font-bold">{totTende}</p>
                <p className="text-xs text-slate-500">Tende Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{totPosti}</p>
                <p className="text-xs text-slate-500">Posti Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg"><Users className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold">{totDisponibili}</p>
                <p className="text-xs text-slate-500">Posti Disponibili</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg"><Users className="h-5 w-5 text-orange-600" /></div>
              <div>
                <p className="text-2xl font-bold">{totOccupati}</p>
                <p className="text-xs text-slate-500">Posti Occupati</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tende List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Elenco Tende</CardTitle>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) { setSelectedTenda(null); resetForm(); }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-tenda-btn">
                <Plus className="h-4 w-4 mr-2" />Nuova Tenda
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{selectedTenda ? "Modifica Tenda" : "Nuova Tenda"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Codice *</Label>
                    <Input value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })} required data-testid="input-codice" placeholder="T001" />
                  </div>
                  <div>
                    <Label>Posti Totali *</Label>
                    <Input type="number" min="1" value={form.posti_totali} onChange={(e) => setForm({ ...form, posti_totali: parseInt(e.target.value) || 1 })} required />
                  </div>
                </div>
                <div>
                  <Label>Modello *</Label>
                  <Input value={form.modello} onChange={(e) => setForm({ ...form, modello: e.target.value })} required placeholder="Es: Ferrino Proxes 4" />
                </div>
                <div>
                  <Label>Ubicazione</Label>
                  <Input value={form.ubicazione} onChange={(e) => setForm({ ...form, ubicazione: e.target.value })} placeholder="Es: Settore A, Fila 2" />
                </div>
                <div>
                  <Label>Note</Label>
                  <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="submit-tenda-btn">Salva</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tende-list">
            {tendeData?.map(t => (
              <Card key={t.id} className="border" data-testid={`tenda-card-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-50 rounded-lg"><Tent className="h-6 w-6 text-purple-600" /></div>
                      <div>
                        <h3 className="font-bold font-mono">{t.codice}</h3>
                        <p className="text-sm text-slate-500">{t.modello}</p>
                      </div>
                    </div>
                    <Badge className={t.posti_occupati >= t.posti_totali ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                      {t.posti_occupati >= t.posti_totali ? "Completa" : "Disponibile"}
                    </Badge>
                  </div>
                  <div className="mt-4 space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" /> Occupazione</span>
                        <span>{t.posti_occupati || 0}/{t.posti_totali}</span>
                      </div>
                      <Progress value={((t.posti_occupati || 0) / t.posti_totali) * 100} className={`h-2 ${getOccupancyColor(t.posti_occupati || 0, t.posti_totali)}`} />
                    </div>
                    {t.ubicazione && (
                      <div className="text-sm text-slate-600 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{t.ubicazione}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(t)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { if (window.confirm("Eliminare questa tenda? Le persone assegnate verranno rimosse.")) deleteMutation.mutate(t.id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {(!tendeData || tendeData.length === 0) && (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Tent className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nessuna tenda registrata</p>
                <p className="text-sm mt-2">Clicca su "Nuova Tenda" per aggiungerne una</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
